import { useCompare } from '@iheartjane/menu';
import type { ReactNode, RefObject } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

const SCROLL_DISMISS_THRESHOLD_PX = 8;

/**
 * Tracks whether a control has revealed the Compare Bar on the current page.
 *
 * On mobile PDP the bar is rendered collapsed so it doesn't cover
 * the fixed add-to-bag section. Controls reveal it on demand, and it hides
 * again on scroll, tap outside, or navigation. Compare mode (`isActive`) itself is untouched.
 */
interface CompareBarVisibilityActions {
  hideBar: () => void;
  revealBar: () => void;
}

const noop = () => undefined;

const ActionsContext = createContext<CompareBarVisibilityActions>({
  hideBar: noop,
  revealBar: noop,
});

const RevealedContext = createContext(false);

export const CompareBarVisibilityProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { pathname } = useLocation();
  // What page the bar was revealed on.
  const [revealedPathname, setRevealedPathname] = useState<string>();
  const isRevealed = revealedPathname === pathname;

  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const actions = useMemo<CompareBarVisibilityActions>(
    () => ({
      hideBar: () => setRevealedPathname(undefined),
      revealBar: () => setRevealedPathname(pathnameRef.current),
    }),
    []
  );

  return (
    <ActionsContext.Provider value={actions}>
      <RevealedContext.Provider value={isRevealed}>
        {children}
      </RevealedContext.Provider>
    </ActionsContext.Provider>
  );
};

export const useCompareBarVisibilityActions = (): CompareBarVisibilityActions =>
  useContext(ActionsContext);

/** Volatile — subscribing re-renders the consumer. */
export const useIsCompareBarRevealed = () => useContext(RevealedContext);

/** Hides a revealed bar on scroll or a tap outside of `barRef`. */
export const useCompareBarDismissal = (
  barRef: RefObject<HTMLElement | null>
) => {
  const { isModalOpen } = useCompare();
  const { hideBar } = useCompareBarVisibilityActions();
  const isRevealed = useIsCompareBarRevealed();

  useEffect(() => {
    if (!isRevealed || isModalOpen) return;

    const initialScrollY = window.scrollY;

    const handleScroll = () => {
      if (
        Math.abs(window.scrollY - initialScrollY) > SCROLL_DISMISS_THRESHOLD_PX
      ) {
        hideBar();
      }
    };

    const handleOutsideClick = ({ target }: Event) => {
      const tappedInsideBar =
        target instanceof Node && (barRef.current?.contains(target) ?? false);

      if (!tappedInsideBar) hideBar();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleOutsideClick, true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [barRef, hideBar, isModalOpen, isRevealed]);
};
